import { getCookie } from '@susu/cookie'

let ga4MeasurementId: string | undefined

/**
 * Sets the GA4 measurement ID (e.g. 'G-XXXXXXXXXX'), used to derive the
 * `_ga_<container-id>` session cookie name. Call once during initialization.
 */
export const setGA4MeasurementId = (id: string): void => {
  ga4MeasurementId = id
}

/**
 * The environments an analytics surface can run in. `cn_*` are the China
 * deployments, which share the global GA4 properties.
 */
export type AnalyticsEnvironment =
  | 'dev'
  | 'tst'
  | 'acc'
  | 'prd'
  | 'cn_tst'
  | 'cn_prd'

/**
 * GA4 is one property per environment, identical for every application — the
 * Segment source an app writes to does not change which property it feeds. So
 * the measurement ID is keyed by environment alone.
 *
 * This replaces a per-write-key table that fell back to the production ID for
 * any key it did not know. That fallback silently pointed unmapped sources —
 * among them the whole In Store Webviews set — at the production
 * `_ga_2JHV4W0XRX` cookie while running in testing and acceptance.
 */
const GA4_MEASUREMENT_ID_BY_ENVIRONMENT: Record<AnalyticsEnvironment, string> =
  {
    dev: 'G-52YBTD282B',
    tst: 'G-52YBTD282B',
    acc: 'G-0XNN95Z7VJ',
    prd: 'G-2JHV4W0XRX',
    cn_tst: 'G-52YBTD282B',
    cn_prd: 'G-2JHV4W0XRX',
  }

/**
 * Resolves the GA4 measurement ID for an environment, used to derive the
 * `_ga_<container-id>` session cookie name. Total over the environment union —
 * there is no fallback to guess wrong with.
 */
export const resolveGA4MeasurementIdForEnvironment = (
  environment: AnalyticsEnvironment,
): string => GA4_MEASUREMENT_ID_BY_ENVIRONMENT[environment]

// ── FC-183 compatibility, release only (DA-1252) ─────────────────────────────
// The per-write-key table the old API used, unchanged, for the apps on release
// that still pass their own write key. Removed by the stack's cleanup PR.
const GA4_MEASUREMENT_ID_BY_SEGMENT_WRITE_KEY: Record<string, string> = {
  Zy9h9uXcEt0hpJd9Xlz3AKbISpOdqBmN: 'G-52YBTD282B', // testing
  vu6HG9YzL0vWMDsSiBz9jbF3TNXuU2US: 'G-0XNN95Z7VJ', // acceptance
}
const PRODUCTION_SEGMENT_WRITE_KEY = '4OhwIsrqFzQ0aeuYev8muGSWiimGtGIG'
const PRODUCTION_GA4_MEASUREMENT_ID = 'G-2JHV4W0XRX'

/**
 * Resolves a Segment write key to its environment's GA4 measurement ID.
 * Unrecognized keys fall back to production, and warn when the key is not
 * production's either.
 * @deprecated FC-183 compat. Use {@link resolveGA4MeasurementIdForEnvironment}.
 */
export const resolveGA4MeasurementIdForSegmentWriteKey = (
  segmentWriteKey: string,
): string => {
  const mappedId = GA4_MEASUREMENT_ID_BY_SEGMENT_WRITE_KEY[segmentWriteKey]
  if (mappedId) return mappedId

  if (segmentWriteKey !== PRODUCTION_SEGMENT_WRITE_KEY) {
    console.warn(
      '[ga4] Unmapped Segment write key for GA4 fallback — defaulting to production. ' +
        'Add it to GA4_MEASUREMENT_ID_BY_SEGMENT_WRITE_KEY if this is a real non-production environment.',
    )
  }
  return PRODUCTION_GA4_MEASUREMENT_ID
}

/**
 * Reads the GA4 client ID directly from the `_ga` cookie
 * (format: `GA1.1.<clientId part 1>.<clientId part 2>`).
 * @returns The client ID, or an empty string if the cookie is missing or malformed
 */
export const getGA4ClientIdFromCookie = (): string => {
  if (typeof window === 'undefined') {
    return ''
  }

  const value = getCookie('_ga')
  if (!value) {
    return ''
  }

  const parts = value.split('.')
  return parts.length >= 4 ? parts.slice(2).join('.') : ''
}

/**
 * Reads the GA4 session ID and session number directly from the
 * `_ga_<container-id>` cookie, handling both the GS2 (May 2025+,
 * dollar-delimited) and GS1 (legacy, dot-delimited) formats.
 *
 * The measurement ID is required. This used to fall back to scanning the jar
 * for any `_ga_*` cookie, which returned whichever container happened to come
 * first — on a page carrying a second container (a GTM-loaded tag, or a
 * surface that resolved the wrong environment) that is a coin flip, and the
 * session read could come from a property we do not own. One environment, one
 * container, one cookie: an unknown ID reads nothing.
 *
 * @param measurementId - GA4 measurement ID (e.g. 'G-XXXXXXXXXX')
 * @returns The session ID and session number, or empty strings when the
 * measurement ID is unknown or the cookie is missing
 */
export const getGA4SessionDataFromCookie = (
  measurementId: string,
): { sessionId: string; sessionNumber: string } => {
  if (typeof window === 'undefined' || !measurementId) {
    return { sessionId: '', sessionNumber: '' }
  }

  const value = getCookie(`_ga_${measurementId.replace('G-', '')}`)
  if (!value) {
    return { sessionId: '', sessionNumber: '' }
  }

  if (value.startsWith('GS2.')) {
    const paramsByKey = new Map(
      value
        .split('.')
        .slice(2)
        .join('.')
        .split('$')
        .map(param => [param.charAt(0), param.slice(1)] as const),
    )
    return {
      sessionId: paramsByKey.get('s') ?? '',
      sessionNumber: paramsByKey.get('o') ?? '',
    }
  }

  // GS1 (legacy): GS1.1.{sessionId}.{sessionNumber}.{...}
  const parts = value.split('.')
  return { sessionId: parts[2] ?? '', sessionNumber: parts[3] ?? '' }
}

/**
 * Get Google Analytics client/session data, read live from GA4's own cookies
 * (`_ga`, `_ga_<container-id>`) rather than a cached snapshot, so every event
 * reflects gtag's current session — including after GA rotates the session
 * cookie following 30 minutes of inactivity.
 */
export const getGoogleAnalyticsData = (): {
  ga_client_id: string
  ga_session_id: string
  ga_session_number: string
} => {
  if (typeof window === 'undefined') {
    return { ga_client_id: '', ga_session_id: '', ga_session_number: '' }
  }

  // Before setGA4MeasurementId runs there is no container to read, so the
  // session fields come back empty rather than from whichever `_ga_*` cookie
  // happened to be first (see DA-1340 for the ordering, and the container
  // mix-up that the any-cookie scan used to cause).
  const { sessionId, sessionNumber } = getGA4SessionDataFromCookie(
    ga4MeasurementId ?? '',
  )

  return {
    ga_client_id: getGA4ClientIdFromCookie(),
    ga_session_id: sessionId,
    ga_session_number: sessionNumber,
  }
}
